
import * as React from "react"
import * as HoverCardPrimitive from "@radix-ui/react-hover-card"

import { cn } from "@/lib/utils"

const HoverCard = HoverCardPrimitive.Root

const HoverCardTrigger = HoverCardPrimitive.Trigger

const HoverCardContent = React.forwardRef<
  React.ElementRef<typeof HoverCardPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Content>
>(({ className, align = "center", sideOffset = 4, ...props }, ref) => {
  // Track if the component is open
  const [isOpen, setIsOpen] = React.useState(false);
  
  // Create a ref to track the initial mount
  const isMountedRef = React.useRef(false);
  
  // Handle state change with a MutationObserver
  React.useEffect(() => {
    if (isMountedRef.current) {
      const handleOpenChange = (open: boolean) => {
        setIsOpen(open);
      };
      
      // Add event listener for state changes
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.attributeName === 'data-state') {
            const state = (mutation.target as HTMLElement).getAttribute('data-state');
            handleOpenChange(state === 'open');
          }
        }
      });
      
      // Start observing the Content element after it's rendered
      const timeoutId = setTimeout(() => {
        // Fixed: Check if ref is a function or an object with current property
        if (typeof ref === 'function') {
          // We can't access .current on a function ref
          console.warn('Function refs are not supported for hover card state tracking');
        } else if (ref && ref.current) {
          observer.observe(ref.current, { attributes: true });
        }
      }, 0);
      
      return () => {
        clearTimeout(timeoutId);
        observer.disconnect();
      };
    } else {
      isMountedRef.current = true;
    }
  }, [ref]);
  
  return (
    <HoverCardPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        "z-50 w-64 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none",
        // Enhanced animations with better performance
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
        "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        // Apply a more subtle shadow and border treatment
        "border-purple-500/20 backdrop-blur-sm backdrop-saturate-150",
        // Apply conditional micro-interactions
        isOpen ? "ring-1 ring-purple-500/30" : "",
        className
      )}
      style={{
        // Enable hardware acceleration for smoother animations
        willChange: isOpen ? 'transform, opacity' : 'auto'
      }}
      {...props}
    />
  );
})
HoverCardContent.displayName = HoverCardPrimitive.Content.displayName

export { HoverCard, HoverCardTrigger, HoverCardContent }
